<template>
    <div class="container-fluid px-5 mt-4">

        <!-- Nadpis + akce -->
        <div class="d-flex align-items-center justify-content-between mb-4 detail-page-header">
            <div class="d-flex align-items-center gap-3">
                <button class="btn file-detail__back-btn" @click="$router.back()">
                    <img :src="iconChevronLeft" alt="">
                </button>
                <h1 class="credits-page__title mb-0">{{ $t('file.fileDetail') }}</h1>
                <span v-if="file.name" class="cert-name-badge">{{ file.name }}</span>
            </div>
            <div class="d-flex gap-2 detail-page-header__actions">
                <button class="btn file-detail__delete-btn" @click="deleteFile()">
                    {{ $t('file.deleteFile') }}
                    <span class="file-detail__delete-icon" :style="{ maskImage: `url(${iconTrash})`, webkitMaskImage: `url(${iconTrash})` }"></span>
                </button>
                <button class="btn file-detail__sharing-btn" @click="toggleSharingModal()">
                    {{ $t('file.shareFile') }}
                    <img :src="iconSharing" alt="" class="file-detail__sharing-icon">
                </button>
            </div>
        </div>

        <Skeletor v-if="loading" />

        <div v-if="!loading">

            <!-- Dvě info karty -->
            <div class="row g-3 mb-4">

                <!-- Karta 1: Informace o souboru -->
                <div class="col-12 col-xl-6">
                    <div class="dashboard-section-header mb-3">
                        <div class="d-flex align-items-center gap-2">
                            <img :src="iconFileInfo" alt="" class="dashboard-section-header__icon">
                            <span class="dashboard-section-header__title">{{ $t('file.fileInfo') }}</span>
                        </div>
                    </div>
                    <div class="file-detail-card">
                        <div class="file-detail-card__body">
                            <div class="row g-0">
                                <div class="col-12 col-sm-6">
                                    <div class="profile-data-field mb-4">
                                        <span class="cert-list__label">{{ $t('file.fileName') }}</span>
                                        <span class="cert-list__value">{{ file.name }}</span>
                                    </div>
                                    <div class="profile-data-field">
                                        <span class="cert-list__label">{{ $t('file.folder') }}</span>
                                        <span class="cert-list__value">{{ file.folder_name ?? '—' }}</span>
                                    </div>
                                </div>
                                <div class="col-12 col-sm-6">
                                    <div class="profile-data-field mb-4">
                                        <span class="cert-list__label">{{ $t('file.fileCreatedDate') }}</span>
                                        <span class="cert-list__value">{{ formatDateShort(file.created_at) }}, {{ formatTime(file.created_at) }}</span>
                                    </div>
                                    <div class="profile-data-field">
                                        <span class="cert-list__label">{{ $t('cert.certName') }}</span>
                                        <div class="d-flex align-items-center gap-2">
                                            <span class="cert-list__value">{{ file.cert?.name ?? '—' }}</span>
                                            <router-link
                                                v-if="file.cert_id"
                                                :to="{ name: 'CertDetail', params: { uuid: file.cert?.uuid } }"
                                                class="cert-info-file-chevron"
                                            >
                                                <img :src="iconChevronRight" alt="" style="height:14px;">
                                            </router-link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="!file.cert_id" class="file-detail-card__footer">
                            <button
                                class="btn cert-list__view-btn"
                                @click="generateCert()"
                                :disabled="generatingCert"
                            >{{ generatingCert ? $t('cert.generatingCert') : $t('cert.generateCert') }}</button>
                        </div>
                    </div>
                </div>

                <!-- Karta 2: Informace o sdílení -->
                <div class="col-12 col-xl-6">
                    <div class="dashboard-section-header mb-3">
                        <div class="d-flex align-items-center gap-2">
                            <img :src="iconSharingChain" alt="" class="dashboard-section-header__icon">
                            <span class="dashboard-section-header__title">{{ $t('file.sharingInfo') }}</span>
                        </div>
                    </div>
                    <div class="file-detail-card">
                        <div class="file-detail-card__body">
                            <div class="row g-0">
                                <div class="col-12 col-sm-6">
                                    <div class="profile-data-field mb-4">
                                        <span class="cert-list__label">{{ $t('file.state') }}</span>
                                        <span class="cert-list__value">{{ fileSharing.mode !== 'off' ? $t('file.fileIsShared') : $t('file.fileIsNotShared') }}</span>
                                    </div>
                                    <div class="profile-data-field">
                                        <span class="cert-list__label">{{ $t('file.timeVault') }}</span>
                                        <span class="cert-list__value">{{ fileSharing.time_sharing_active ? $t('general.yes') : $t('general.no') }}</span>
                                    </div>
                                </div>
                                <div class="col-12 col-sm-6">
                                    <div class="profile-data-field mb-4">
                                        <span class="cert-list__label">{{ $t('file.sharingMode') }}</span>
                                        <span class="cert-list__value">{{ translateSharingMode(fileSharing.mode) }}</span>
                                    </div>
                                    <div class="profile-data-field">
                                        <span class="cert-list__label">{{ $t('file.isEncrypted') }}</span>
                                        <span class="cert-list__value">{{ file.file_is_encrypted ? $t('general.yes') : $t('general.no') }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <!-- Sekce náhled souboru -->
            <div class="dashboard-section-header mb-3">
                <div class="d-flex align-items-center gap-2">
                    <img :src="iconSpecialOk" alt="" class="dashboard-section-header__icon">
                    <span class="dashboard-section-header__title">{{ $t('general.preview') }}</span>
                    <span v-if="fileExtension" class="file-ext-badge">{{ fileExtension.toUpperCase() }}</span>
                </div>
            </div>

            <!-- Náhled -->
            <div class="cert-detail__pdf-wrap mb-5">
                <div class="cert-detail__pdf-inner">
                    <div v-if="file.mime_type === 'application/pdf' && pdfSrc">
                        <vue-pdf-embed :source="pdfSrc" />
                    </div>
                    <div v-else-if="file.mime_type && file.mime_type.includes('image/') && file.base64">
                        <img :src="'data:' + file.mime_type + ';base64,' + file.base64" class="w-100" alt="" />
                    </div>
                    <div v-else-if="isOfficeFile">
                        <div class="o365-iframe-wrapp">
                            <iframe
                                :src="'https://view.officeapps.live.com/op/embed.aspx?src=' + file.view_url"
                                frameborder="0"
                                width="100%"
                                height="100%"
                                class="o365-iframe"
                            ></iframe>
                        </div>
                    </div>
                    <div v-else-if="!file.base64 && !isOfficeFile" class="alert alert-warning text-center">
                        {{ $t('file.previewNotAvailable') }}
                    </div>
                </div>
                <div class="d-flex justify-content-center mt-3">
                    <button class="btn file-detail__download-btn" @click="downloadFile()">
                        {{ $t('file.download') }}
                        <img :src="iconDownload" alt="" class="file-detail__download-icon">
                    </button>
                </div>
            </div>

        </div>

        <!-- Modál sdílení -->
        <transition name="page-fade">
        <div v-if="modalSharingActive" class="create-cert-overlay" @click.self="toggleSharingModal">
            <div class="create-cert-modal" style="max-width: 660px;">

                    <!-- Hlavička -->
                    <div class="create-cert-modal__header">
                        <span class="create-cert-modal__title">{{ $t('file.setupSharing') }}</span>
                        <button class="create-cert-modal__close" type="button" @click="toggleSharingModal" aria-label="Close">×</button>
                    </div>

                    <!-- Tělo -->
                    <div class="create-cert-modal__body">
                        <Form @submit="saveSharingOptions" ref="documentSharingForm">

                            <!-- Režim sdílení -->
                            <p class="sharing-modal__section-label">{{ $t('file.sharingMode') }}</p>
                            <div class="sharing-modal__options mb-4">
                                <label class="sharing-modal__option" :class="{ 'sharing-modal__option--active': fileSharing.mode === 'off' }">
                                    <input type="radio" v-model="fileSharing.mode" value="off" class="sharing-modal__radio">
                                    <i class="bi bi-x-circle sharing-modal__option-icon"></i>
                                    <span>{{ $t('file.turnOffSharing') }}</span>
                                </label>
                                <label class="sharing-modal__option" :class="{ 'sharing-modal__option--active': fileSharing.mode === 'link' }">
                                    <input type="radio" v-model="fileSharing.mode" value="link" class="sharing-modal__radio">
                                    <i class="bi bi-link-45deg sharing-modal__option-icon"></i>
                                    <span>{{ $t('file.forAnyoneWithLink') }}</span>
                                </label>
                                <label class="sharing-modal__option" :class="{ 'sharing-modal__option--active': fileSharing.mode === 'logged' }">
                                    <input type="radio" v-model="fileSharing.mode" value="logged" class="sharing-modal__radio">
                                    <i class="bi bi-people sharing-modal__option-icon"></i>
                                    <span>{{ $t('file.forAnyoneWithAppAccess') }}</span>
                                </label>
                            </div>

                            <!-- Time Vault -->
                            <div class="sharing-modal__divider"></div>
                            <p class="sharing-modal__section-label mt-4">{{ $t('file.timeVault') }}</p>
                            <label class="create-cert-label" for="delayInHours">{{ $t('file.sharingAndNotificationDelay') }}</label>
                            <Field
                                v-model="fileSharing.delay_notification"
                                type="number"
                                class="create-cert-input mb-3"
                                name="delayInHours"
                                id="delayInHours"
                                :placeholder="$t('file.notificationDelay')"
                            />
                            <div class="form-check form-switch mb-3">
                                <input class="form-check-input" type="checkbox" id="timeSharingActive" v-model="fileSharing.time_sharing_active">
                                <label class="form-check-label" for="timeSharingActive">{{ $t('file.sharingFromTo') }}</label>
                            </div>
                            <div v-if="fileSharing.time_sharing_active" class="row g-3 mb-3">
                                <div class="col-12 col-md-6">
                                    <label class="create-cert-label" for="sharingFrom">{{ $t('file.shareFrom') }}</label>
                                    <Field v-model="fileSharing.sharing_from" type="datetime-local" class="create-cert-input" name="sharingFrom" id="sharingFrom" />
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="create-cert-label" for="sharingTo">{{ $t('file.shareTo') }}</label>
                                    <Field v-model="fileSharing.sharing_to" type="datetime-local" class="create-cert-input" name="sharingTo" id="sharingTo" />
                                </div>
                            </div>

                            <!-- Šifrování -->
                            <div class="sharing-modal__divider"></div>
                            <p class="sharing-modal__section-label mt-4">{{ $t('file.encryption') }}</p>
                            <div class="sharing-modal__options mb-2">
                                <label class="sharing-modal__option" :class="{ 'sharing-modal__option--active': fileSharing.encryption === 'none' }">
                                    <input type="radio" v-model="fileSharing.encryption" value="none" class="sharing-modal__radio">
                                    <i class="bi bi-unlock sharing-modal__option-icon"></i>
                                    <span>{{ $t('file.notEncrypt') }}</span>
                                </label>
                                <label class="sharing-modal__option" :class="{ 'sharing-modal__option--active': fileSharing.encryption === 'shared_key', 'sharing-modal__option--disabled': encryptionSelectDisabled }">
                                    <input type="radio" v-model="fileSharing.encryption" value="shared_key" class="sharing-modal__radio" :disabled="encryptionSelectDisabled">
                                    <i class="bi bi-key sharing-modal__option-icon"></i>
                                    <span>{{ $t('file.encryptWithSharedKey') }}</span>
                                </label>
                                <label class="sharing-modal__option" :class="{ 'sharing-modal__option--active': fileSharing.encryption === 'unique_key', 'sharing-modal__option--disabled': encryptionSelectDisabled }">
                                    <input type="radio" v-model="fileSharing.encryption" value="unique_key" class="sharing-modal__radio" :disabled="encryptionSelectDisabled">
                                    <i class="bi bi-shield-lock sharing-modal__option-icon"></i>
                                    <span>{{ $t('file.encryptWithUniqueKey') }}</span>
                                </label>
                            </div>
                            <small class="text-danger d-block mb-4">{{ $t('file.encryptionWarning') }}</small>

                            <!-- Sdílet s -->
                            <div class="sharing-modal__divider"></div>
                            <div class="d-flex align-items-center justify-content-between mt-4 mb-3">
                                <p class="sharing-modal__section-label mb-0">{{ $t('file.shareWith') }}</p>
                                <button class="btn cert-list__view-btn" type="button" @click.prevent="startNewSharingInputs">
                                    <i class="bi bi-plus-lg me-1"></i>{{ $t('file.addSharing') }}
                                </button>
                            </div>

                            <div v-if="fileSharing.delay_notification && fileSharing['emails']?.[0]" class="form-check mb-3">
                                <input class="form-check-input" type="checkbox" id="ignoreDelay" v-model="fileSharing.ignore_delay">
                                <label class="form-check-label" for="ignoreDelay">{{ $t('ignoreSharingDelay') }}</label>
                            </div>

                            <div v-if="showNewShareInputs" class="mb-4">
                                <div class="mb-2" v-for="(email, idx) in fileSharing['emails']" :key="'email-input-'+idx">
                                    <div class="d-flex align-items-center gap-2">
                                        <input
                                            type="email"
                                            :class="['create-cert-input flex-grow-1', { 'is-invalid': isDuplicateEmail(idx) }]"
                                            :placeholder="'E-mail ' + (idx + 1)"
                                            v-model="fileSharing['emails'][idx]"
                                            @blur="onEmailBlur(idx)"
                                        />
                                        <button v-if="idx === fileSharing['emails'].length - 1" class="btn cert-list__view-btn" type="button" @click.prevent="addEmailInput">
                                            <i class="bi bi-plus-lg"></i>
                                        </button>
                                        <button v-else class="btn cert-detail__delete-btn" type="button" @click.prevent="removeEmailInput(idx)">
                                            <i class="bi bi-dash-lg"></i>
                                        </button>
                                    </div>
                                    <div v-if="isDuplicateEmail(idx)" class="text-danger small mt-1">{{ $t('file.duplicatedEmail') }}</div>
                                </div>
                            </div>

                            <!-- Tabulka existujících sdílení -->
                            <div v-if="fileShares.length" class="sharing-modal__table-wrap mb-4">
                                <table class="sharing-modal__table">
                                    <thead>
                                        <tr>
                                            <th>{{ $t('file.email') }}</th>
                                            <th>{{ $t('file.state') }}</th>
                                            <th>{{ $t('file.lastViewAt') }}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="(share, index) in fileShares"
                                            :key="index"
                                            :class="{ 'sharing-modal__table-row--inactive': share.deleted_at }"
                                        >
                                            <td>{{ share.email }}</td>
                                            <td>
                                                <span :class="share.is_active ? 'sharing-modal__badge sharing-modal__badge--active' : 'sharing-modal__badge sharing-modal__badge--inactive'">
                                                    {{ share.is_active ? $t('file.active') : $t('file.inactive') }}
                                                </span>
                                                <span class="tooltip-wrapper ms-1" v-if="share.is_active">
                                                    <i class="bi bi-info-circle text-muted"></i>
                                                    <span class="tooltip-text">{{ (share.active_from ? $t('file.sharedFrom') + ': ' + share.active_from + ', ' : '') + (share.notified_at ? $t('file.notificationSent') + ': ' + share.notified_at : '') }}</span>
                                                </span>
                                            </td>
                                            <td class="text-muted">{{ share.last_viewed_at ?? '—' }}</td>
                                            <td>
                                                <div class="d-flex gap-2 justify-content-end">
                                                    <button
                                                        class="btn cert-list__view-btn"
                                                        type="button"
                                                        @click="resendNotification(share)"
                                                        :title="$t('file.sendNotificationAgain')"
                                                        :disabled="notificationIsSending[share.id] || !share.is_active"
                                                    >
                                                        <i class="bi bi-envelope" v-if="!notificationIsSending[share.id]"></i>
                                                        <span v-else class="spinner-border spinner-border-sm" role="status"></span>
                                                    </button>
                                                    <button class="btn cert-detail__delete-btn" type="button" @click="removeShare(share)" :title="$t('file.removeSharing')">
                                                        <i class="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Uložit -->
                            <div class="sharing-modal__divider mb-4"></div>
                            <div class="d-flex justify-content-end">
                                <button
                                    type="submit"
                                    class="btn btn-primary"
                                    :disabled="hasDuplicateEmails || !showNewShareInputs || !fileSharing.emails || fileSharing.emails.length === 0 || fileSharing.emails[0] === ''"
                                >{{ $t('general.save') }}</button>
                            </div>

                        </Form>
                    </div>
                </div>
        </div>
        </transition>

    </div>
</template>

<script>
import http from '@/http';
import VuePdfEmbed from 'vue-pdf-embed';
import iconFile from '/public/img/new-design/icons/file.svg';
import iconSpecialOk from '/public/img/new-design/icons/special-ok-icon.svg';
import iconDownload from '/public/img/new-design/icons/download.svg';
import iconChevronLeft from '/public/img/new-design/icons/chevron-left.svg';
import iconTrash from '/public/img/new-design/icons/trash.svg';
import iconSharing from '/public/img/new-design/icons/sharing.svg';
import iconFileInfo from '/public/img/new-design/icons/file_info.svg';
import iconSharingChain from '/public/img/new-design/icons/sharing.svg';
import iconChevronRight from '/public/img/new-design/icons/chevron-right.svg';
import { loadingState } from '@/loading.js';

export default {
    components: { VuePdfEmbed },

    mounted() {
        this.getFileDetail();
    },

    data() {
        return {
            file: [],
            loading: true,
            sharesLoading: true,
            generatingCert: false,
            modalSharingActive: false,
            showNewShareInputs: false,
            fileShares: [],
            fileSharing: {
                mode: 'off',
                delay_notification: 0,
                ignore_delay: false,
                time_sharing_active: false,
                sharing_from: '',
                sharing_to: '',
                emails: [],
                encryption: 'none',
            },
            encryptionSelectDisabled: false,
            notificationIsSending: [],
            iconFile,
            iconSpecialOk,
            iconDownload,
            iconChevronLeft,
            iconTrash,
            iconSharing,
            iconFileInfo,
            iconSharingChain,
            iconChevronRight,
        };
    },

    computed: {
        fileExtension() {
            if (this.file.extension) return this.file.extension;
            const name = this.file.filename || this.file.name || '';
            const dot = name.lastIndexOf('.');
            return dot !== -1 ? name.slice(dot + 1) : '';
        },
        pdfSrc() {
            if (this.file && this.file.mime_type === 'application/pdf' && this.file.base64) {
                return { data: window.atob(this.file.base64) };
            }
            return null;
        },
        isOfficeFile() {
            const officeMimes = [
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'application/vnd.ms-powerpoint',
                'application/vnd.ms-excel',
                'application/msword',
            ];
            return this.file && officeMimes.includes(this.file.mime_type);
        },
        hasDuplicateEmails() {
            if (this.fileSharing && !Array.isArray(this.fileSharing['emails'])) return true;
            const normalized = this.fileSharing['emails']
                .map(e => (e || '').trim().toLowerCase())
                .filter(e => e);
            return new Set(normalized).size !== normalized.length;
        },
    },

    methods: {
        getFileDetail() {
            http.get('/files/' + this.$route.params.uuid + '/detail').then((response) => {
                this.file = response.data.file;
                this.fileSharing = response.data.file.sharing_settings ?? this.fileSharing;
                this.encryptionSelectDisabled = this.file.file_is_encrypted;
                this.getFileShares();
                if (this.$route.query.openEncryption === '1') {
                    this.$nextTick(() => this.toggleSharingModal());
                }
            }).catch(() => {}).finally(() => {
                this.loading = false;
                loadingState.active = false;
            });
        },

        getFileShares() {
            http.get('/files/' + this.$route.params.uuid + '/shares').then((response) => {
                this.fileShares = response.data.file_shares;
                this.sharesLoading = false;
            });
        },

        resendNotification(share) {
            this.notificationIsSending[share.id] = true;
            http.get('/files/' + this.$route.params.uuid + '/shares/' + share.id + '/send-notification').then(() => {
                this.notificationIsSending[share.id] = false;
            });
        },

        removeShare(share) {
            this.$swal.fire({
                title: this.$t('file.sharingWillBeRemoved'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#175cff',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('general.yes'),
                cancelButtonText: this.$t('general.no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    http.delete('/files/' + this.file.uuid + '/shares/' + share.id).then(() => {
                        share.deleted_at = new Date();
                        share.is_active = false;
                    });
                }
            });
        },

        generateCert() {
            this.generatingCert = true;
            http.post('/certs/generate', { file_model: this.file }).then((response) => {
                this.file = response.data.file;
                if (window.updateCreditCount) window.updateCreditCount();
                this.generatingCert = false;
            });
        },

        deleteCert() {
            this.$swal.fire({
                title: this.$t('cert.confirmDelete'),
                icon: 'warning',
                input: this.file?.cert_uuid ? 'checkbox' : false,
                inputValue: 1,
                inputPlaceholder: this.$t('cert.fileDeleteToo'),
                showCancelButton: true,
                confirmButtonColor: '#175cff',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('general.yes'),
                cancelButtonText: this.$t('general.no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    const deleteFile = !result.value;
                    http.post('/certs/' + this.file.cert_uuid + '/delete?deleteOnlyCert=' + deleteFile).then(() => {
                        if (!deleteFile) {
                            this.$router.push({ name: 'Dashboard' });
                        } else {
                            this.file.cert_id = null;
                            this.file.cert_uuid = null;
                            this.file.cert = null;
                        }
                    });
                }
            });
        },

        downloadCert() {
            http.post('/certs/' + this.file.cert_uuid + '/download', { responseType: 'blob' }).then((response) => {
                const fileURL = `data:application/pdf;base64,${response.data}`;
                const link = document.createElement('a');
                link.href = fileURL;
                link.setAttribute('download', 'Certificate.pdf');
                document.body.appendChild(link);
                link.click();
                link.remove();
            });
        },

        deleteFile() {
            this.$swal.fire({
                title: this.$t('file.confirmDelete'),
                icon: 'warning',
                input: this.file?.cert_uuid ? 'checkbox' : false,
                inputValue: 1,
                inputPlaceholder: this.$t('file.certDeleteToo'),
                showCancelButton: true,
                confirmButtonColor: '#175cff',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('general.yes'),
                cancelButtonText: this.$t('general.no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    const deleteCert = !result.value;
                    http.post('/files/' + this.$route.params.uuid + '/delete?deleteOnlyFile=' + deleteCert).then(() => {
                        this.$router.push({ name: 'Dashboard' });
                    });
                }
            });
        },

        downloadFile() {
            http.get('/files/' + this.$route.params.uuid + '/view?download=true', { responseType: 'blob' }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', this.file.filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
        },

        saveSharingOptions() {
            this.$swal.fire({
                title: this.$t('file.confirmSharing'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#175cff',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('general.yes'),
                cancelButtonText: this.$t('general.no'),
            }).then((result) => {
                if (result.isConfirmed) {
                    http.post('/files/' + this.$route.params.uuid + '/sharing', this.fileSharing).then((response) => {
                        this.file = response.data.file;
                        this.fileSharing = response.data.file.sharing_settings;
                        this.getFileShares();
                        this.toggleSharingModal();
                    });
                }
            });
        },

        toggleSharingModal() {
            this.modalSharingActive = !this.modalSharingActive;
            document.body.style.overflow = this.modalSharingActive ? 'hidden' : '';
        },

        startNewSharingInputs() {
            this.showNewShareInputs = true;
            if (!this.fileSharing['emails'] || this.fileSharing['emails'].length === 0) {
                this.fileSharing['emails'] = [''];
            }
        },

        addEmailInput() {
            if (!Array.isArray(this.fileSharing['emails'])) {
                this.fileSharing['emails'] = [];
            }
            this.fileSharing['emails'].push('');
        },

        removeEmailInput(idx) {
            if (Array.isArray(this.fileSharing['emails'])) {
                this.fileSharing['emails'].splice(idx, 1);
            }
        },

        normalizeEmail(email) {
            return (email || '').trim().toLowerCase();
        },

        isDuplicateEmail(idx) {
            const emails = this.fileSharing['emails'];
            if (!Array.isArray(emails)) return false;
            const current = this.normalizeEmail(emails[idx]);
            if (!current) return false;
            for (let i = 0; i < idx; i++) {
                if (this.normalizeEmail(emails[i]) === current) return true;
            }
            return false;
        },

        onEmailBlur(idx) {
            if (!Array.isArray(this.fileSharing['emails'])) return;
            this.fileSharing['emails'][idx] = this.normalizeEmail(this.fileSharing['emails'][idx]);
        },

        translateSharingMode(mode) {
            switch (mode) {
                case 'off':   return this.$t('file.turnOffSharing');
                case 'link':  return this.$t('file.forAnyoneWithLink');
                case 'logged': return this.$t('file.forAnyoneWithAppAccess');
                default:      return mode;
            }
        },

        formatDateShort(dateStr) {
            const d = new Date(dateStr);
            const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
            return `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
        },

        formatTime(dateStr) {
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
    },
};
</script>

<style>
.file-ext-badge {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 6px;
    background: var(--bg-brand);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    border-radius: 4px;
}

.file-detail__back-btn {
    width: 40px;
    height: 40px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 1px solid #D0D4E3;
    background: #fff;
    border-radius: 8px;
}
.file-detail__back-btn img {
    height: 20px;
    width: auto;
}

.file-detail__delete-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 500;
    color: #c0392b;
    background: transparent;
    border: none;
    border-radius: 8px;
}
.file-detail__delete-btn:hover {
    color: #a93226;
    background: transparent;
}
.file-detail__delete-icon {
    display: inline-block;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    background-color: #c0392b;
    mask-repeat: no-repeat;
    mask-size: contain;
    mask-position: center;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-size: contain;
    -webkit-mask-position: center;
}

.file-detail__sharing-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    background: var(--bg-brand);
    border: none;
    border-radius: 8px;
}
.file-detail__sharing-btn:hover {
    color: #fff;
    background: #0047cc;
}
.file-detail__sharing-icon {
    height: 15px;
    width: auto;
    filter: brightness(0) invert(1);
}

.file-detail__download-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 24px;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    background: var(--bg-brand);
    border: none;
    border-radius: 8px;
}
.file-detail__download-btn:hover {
    color: #fff;
    background: #0047cc;
}
.file-detail__download-icon {
    height: 15px;
    width: auto;
    filter: brightness(0) invert(1);
}

.o365-iframe-wrapp {
    position: relative;
}
.o365-iframe-wrapp:after {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 40px;
    width: 100%;
    background: #fff;
    display: block !important;
    content: '';
}

/* Sharing modal */
.sharing-modal__section-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7D7E91;
    margin-bottom: 10px;
}

.sharing-modal__divider {
    height: 1px;
    background: #E9EBF0;
    margin-left: -24px;
    margin-right: -24px;
}

.sharing-modal__options {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.sharing-modal__radio {
    display: none;
}

.sharing-modal__option {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 48px;
    padding: 0 16px;
    border: 1px solid #D0D4E3;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-size: 14px;
    color: #101827;
    margin: 0;
    transition: border-color 0.15s, background 0.15s;
}
.sharing-modal__option:hover {
    border-color: #aab;
}
.sharing-modal__option--active {
    border-color: #175cff;
    background: #f0f5ff;
}
.sharing-modal__option--disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.sharing-modal__option-icon {
    font-size: 17px;
    color: #7D7E91;
    flex-shrink: 0;
}
.sharing-modal__option--active .sharing-modal__option-icon {
    color: #175cff;
}

/* Tabulka sdílení */
.sharing-modal__table-wrap {
    border: 1px solid #E9EBF0;
    border-radius: 8px;
    overflow: hidden;
}
.sharing-modal__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}
.sharing-modal__table th {
    background: #F8F8F8;
    color: #7D7E91;
    font-weight: 500;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 8px 12px;
    border-bottom: 1px solid #E9EBF0;
    text-align: left;
}
.sharing-modal__table td {
    padding: 10px 12px;
    border-bottom: 1px solid #F0F2F5;
    color: #101827;
    vertical-align: middle;
}
.sharing-modal__table tr:last-child td {
    border-bottom: none;
}
.sharing-modal__table-row--inactive {
    opacity: 0.45;
    text-decoration: line-through;
}

.sharing-modal__badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
}
.sharing-modal__badge--active {
    background: #e6f9ee;
    color: #1a9e54;
}
.sharing-modal__badge--inactive {
    background: #fde8e8;
    color: #c0392b;
}

.file-detail-card {
    background: #F8F8F8;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}
.file-detail-card__body {
    padding: 24px;
    flex: 1;
}
.file-detail-card__footer {
    padding: 14px 24px;
    border-top: 1px solid #E9EBF0;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}
</style>